import { CancelledError } from "@tanstack/react-query";
import type { Query, QueryClient, QueryFilters } from "@tanstack/react-query";
import { storyQueryKeys, type StoryQueryKey } from "./storyQueryKeys";

export type StoryMutationEvent =
  | { kind: "source-change"; propertyId: string }
  | { kind: "measurement-change"; propertyIds: readonly string[] }
  | { kind: "plan-change"; propertyId: string }
  | { kind: "offer-tender-change"; propertyId: string }
  | { kind: "relationship-change"; propertyId: string; sessionKey: string }
  | { kind: "package-change"; sessionKey: string }
  | { kind: "pat-change"; sessionKey: string };

export interface StoryInvalidationOptions {
  /**
   * `active` refreshes mounted consumers before the promise resolves. Use
   * `none` when a successful mutation immediately navigates away: queries are
   * marked stale first and the destination owns the next fetch.
   */
  readonly refetchType?: "active" | "none";
  /** Query subtrees already refreshed/seeded by the caller. */
  readonly excludeQueryKeys?: readonly StoryQueryKey[];
  /**
   * Exact active snapshots that must be fresh before a revision-bound command
   * may be reviewed again. All affected consumers are still marked stale, but
   * recovery does not wait for unrelated mounted views to refetch.
   */
  readonly refetchQueryKeys?: readonly StoryQueryKey[];
}

function uniqueKeys(keys: readonly StoryQueryKey[]): readonly StoryQueryKey[] {
  const seen = new Set<string>();
  return keys.filter((key) => {
    const serialized = JSON.stringify(key);
    if (seen.has(serialized)) return false;
    seen.add(serialized);
    return true;
  });
}

function isQuotaForSession(query: Query, sessionKey: string): boolean {
  const key = query.queryKey;
  return (
    key[0] === "story-server-state" &&
    key[1] === "stand" &&
    key[3] === "source-dependent" &&
    key[4] === "resolved" &&
    key[6] === "access-quota" &&
    key[7] === sessionKey
  );
}

function invalidationFilters(
  event: StoryMutationEvent,
  queryKeys: readonly StoryQueryKey[],
  excludeQueryKeys: readonly StoryQueryKey[] = [],
): QueryFilters[] {
  const isExcluded = (query: Query): boolean =>
    excludeQueryKeys.some((excluded) =>
      excluded.every((segment, index) =>
        Object.is(query.queryKey[index], segment),
      ),
    );
  const filters: QueryFilters[] = queryKeys.map((queryKey) => ({
    queryKey,
    predicate: (query) => !isExcluded(query),
  }));
  if (event.kind === "package-change") {
    filters.push({
      predicate: (query) =>
        !isExcluded(query) && isQuotaForSession(query, event.sessionKey),
    });
  }
  return filters;
}

/** Every owner and consumer affected by a domain mutation, expressed as prefixes. */
export function storyInvalidationKeys(
  event: StoryMutationEvent,
): readonly StoryQueryKey[] {
  if (event.kind === "package-change") {
    return uniqueKeys([
      storyQueryKeys.package.account(event.sessionKey),
      storyQueryKeys.pat.account(event.sessionKey),
      storyQueryKeys.property.portfolio(event.sessionKey),
      storyQueryKeys.property.portfolioRanking(event.sessionKey),
      storyQueryKeys.property.subscriptions(event.sessionKey),
      storyQueryKeys.measurements.all,
      storyQueryKeys.valuation.all,
    ]);
  }
  if (event.kind === "pat-change") {
    return [storyQueryKeys.pat.account(event.sessionKey)];
  }

  if (event.kind === "relationship-change") {
    return uniqueKeys([
      storyQueryKeys.property.scope(event.propertyId),
      storyQueryKeys.property.relationship(event.propertyId, event.sessionKey),
      storyQueryKeys.property.portfolio(event.sessionKey),
      storyQueryKeys.property.portfolioRanking(event.sessionKey),
      storyQueryKeys.valuation.property(event.propertyId),
    ]);
  }

  const propertyConsumers = (propertyId: string): readonly StoryQueryKey[] => [
    storyQueryKeys.property.scope(propertyId),
    storyQueryKeys.property.portfolioRankingAll,
    storyQueryKeys.inventory.property(propertyId),
    storyQueryKeys.map.property(propertyId),
    storyQueryKeys.propertyRisk.property(propertyId),
  ];

  if (event.kind === "source-change") {
    const propertyId = event.propertyId;
    return uniqueKeys([
      ...propertyConsumers(propertyId),
      storyQueryKeys.measurements.atlas(),
      storyQueryKeys.measurements.dashboard(),
      storyQueryKeys.measurements.property(propertyId),
      storyQueryKeys.stand.sourceDependentProperty(propertyId),
      storyQueryKeys.valuation.property(propertyId),
      storyQueryKeys.planning.property(propertyId),
    ]);
  }
  if (event.kind === "measurement-change") {
    const propertyIds = [...new Set(event.propertyIds)];
    return uniqueKeys([
      ...propertyIds.flatMap(propertyConsumers),
      storyQueryKeys.measurements.all,
      storyQueryKeys.campaigns.landingAll,
      ...propertyIds.flatMap((propertyId) => [
        storyQueryKeys.campaigns.property(propertyId),
        storyQueryKeys.valuation.property(propertyId),
        storyQueryKeys.planning.property(propertyId),
        storyQueryKeys.stand.property(propertyId),
      ]),
    ]);
  }
  const propertyId = event.propertyId;
  if (event.kind === "plan-change") {
    return uniqueKeys([
      storyQueryKeys.planning.property(propertyId),
      storyQueryKeys.stand.property(propertyId),
      storyQueryKeys.valuation.property(propertyId),
      storyQueryKeys.map.overlays(propertyId),
      storyQueryKeys.commerce.property(propertyId),
      storyQueryKeys.property.portfolioRankingAll,
    ]);
  }
  return uniqueKeys([
    storyQueryKeys.commerce.property(propertyId),
    storyQueryKeys.planning.property(propertyId),
    storyQueryKeys.valuation.property(propertyId),
    storyQueryKeys.property.portfolioRankingAll,
  ]);
}

/** Invalidate all consuming routes after the mutation has succeeded. */
export async function invalidateStoryMutation(
  queryClient: QueryClient,
  event: StoryMutationEvent,
  options: StoryInvalidationOptions = {},
): Promise<void> {
  const queryKeys = storyInvalidationKeys(event);
  const filters = invalidationFilters(
    event,
    queryKeys,
    options.excludeQueryKeys,
  );
  await Promise.all(
    filters.map((filter) =>
      queryClient.cancelQueries(filter, { silent: true }),
    ),
  );
  await Promise.all(
    filters.map((filter) =>
      queryClient.invalidateQueries({ ...filter, refetchType: "none" }),
    ),
  );
  if (options.refetchType === "none") return;
  const refetchFilters: QueryFilters[] = options.refetchQueryKeys
    ? options.refetchQueryKeys.map((queryKey) => ({
        queryKey,
        exact: true,
        type: "active",
      }))
    : filters.map((filter) => ({ ...filter, type: "active" }));
  await Promise.all(
    refetchFilters.map((filter) =>
      queryClient
        .refetchQueries(filter, { throwOnError: true })
        .catch((error: unknown) => {
          // A refetch that a LATER invalidation cancelled is React Query's own
          // supersede signal, not a fetch failure: this helper opens with
          // `cancelQueries`, so two overlapping story mutations (e.g. the
          // optimized and Pukkala-AAF generated-plan reconciles, which fire
          // together at bulk-batch completion) will always have the second call
          // cancel the first call's in-flight refetches. Rethrowing that as an
          // error rejected the first mutation mid-apply and escaped as an
          // unhandled `CancelledError`. Genuine fetch failures still propagate.
          if (error instanceof CancelledError) return;
          throw error;
        }),
    ),
  );
}
